/*
 * PDP v5 — estimated delivery line.
 *
 * One calculation on load. There is no timer here and there must not be one:
 * a countdown was tried on this page before and removed for being fragile.
 * Nothing in this file re-runs, watches the clock, or updates the DOM after
 * the first paint.
 *
 * It also fails closed. Every exit below leaves the element `hidden`, so a
 * missing setting, an unparseable number or an exception renders no line at
 * all rather than a wrong date. The dispatch line above it already carries
 * the useful information on its own.
 *
 * The cutoff is what decides whether today counts. Before it, on a working
 * day, the parcel leaves today; after it, the next working day. Both the hour
 * and the comparison are the warehouse's, which is why the line above says
 * "order by 10am" without naming a timezone -- see snippets/pdp-dispatch.liquid
 * for why that ambiguity is left in the customer's favour rather than resolved.
 *
 * Dates are handled as UTC midnights: the calendar day is the unit of work,
 * and pinning every Date to 00:00Z keeps day arithmetic free of DST. The only
 * place a real timezone appears is nowInSingapore(), because "what day and
 * hour is it at the warehouse" is a Singapore fact, and the formatter, told
 * to read those UTC midnights back as UTC so a viewer behind UTC does not see
 * yesterday's date.
 */

(function () {
  var WAREHOUSE_TZ = 'Asia/Singapore';

  /* The day and the hour have to be read from the same instant, in the same
     timezone, or an order placed at 23:59 can be dated to one day and clocked
     to the next. formatToParts returns both from one format() call.

     hourCycle h23 because h24 reports midnight as 24, which would read as
     after every cutoff. en-CA is a formatting choice, not a language one: it
     yields YYYY-MM-DD, the format the holiday settings already use and the one
     Date.UTC takes without ambiguity. The visible words come from the viewer's
     own locale further down. */
  function nowInSingapore() {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: WAREHOUSE_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date());

    var found = {};
    parts.forEach(function (part) {
      found[part.type] = part.value;
    });

    var date = fromISO(found.year + '-' + found.month + '-' + found.day);
    if (!date) return null;
    return { date: date, hour: parseInt(found.hour, 10) };
  }

  function fromISO(iso) {
    var bits = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!bits) return null;
    return new Date(Date.UTC(+bits[1], +bits[2] - 1, +bits[3]));
  }

  function toISO(date) {
    return date.toISOString().slice(0, 10);
  }

  function parseHolidays(raw) {
    var set = Object.create(null);
    if (!raw) return set;
    raw.split(',').forEach(function (entry) {
      var iso = entry.trim();
      if (fromISO(iso)) set[iso] = true;
    });
    return set;
  }

  /* Monday to Friday, minus the market's public holidays. An empty holiday
     list is the normal case and costs one lookup. */
  function isWorkingDay(date, holidays) {
    var day = date.getUTCDay();
    if (day === 0 || day === 6) return false;
    return !holidays[toISO(date)];
  }

  function nextWorkingDay(date, holidays) {
    var next = new Date(date.getTime());
    /* A market whose holiday list swallowed a whole year would spin, so the
       walk is bounded. 30 is far past any real run of closed days. */
    for (var i = 0; i < 30; i++) {
      next.setUTCDate(next.getUTCDate() + 1);
      if (isWorkingDay(next, holidays)) return next;
    }
    return null;
  }

  /* Counts from the day AFTER `date`: a parcel picked up on Friday is not
     delivered on Friday, so day 1 of transit is the next working day. */
  function addWorkingDays(date, count, holidays) {
    var cursor = date;
    for (var i = 0; i < count; i++) {
      cursor = nextWorkingDay(cursor, holidays);
      if (!cursor) return null;
    }
    return cursor;
  }

  /* "2" -> [2, 2]; "3-5" and "3–5" -> [3, 5]. Anything else -> null, which
     the caller treats as "render nothing". */
  function parseTransit(raw) {
    if (!raw) return null;
    var bits = String(raw).split(/[-–—]/).map(function (bit) {
      return parseInt(bit.trim(), 10);
    });
    if (bits.length === 1 && bits[0] > 0) return [bits[0], bits[0]];
    if (bits.length === 2 && bits[0] > 0 && bits[1] >= bits[0]) return [bits[0], bits[1]];
    return null;
  }

  /* timeZone: 'UTC' is not decoration. The Date is a UTC midnight, so a
     viewer in UTC-5 formatting it in local time would be shown the previous
     day. The weekday and month words themselves come from the viewer's
     locale -- undefined means "whatever the browser is set to". */
  function format(date, options) {
    var opts = { timeZone: 'UTC' };
    for (var key in options) opts[key] = options[key];
    return date.toLocaleDateString(undefined, opts);
  }

  function singleLine(date) {
    return 'Estimated delivery by ' + format(date, {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  }

  /* Which side the month falls on is the locale's call, not ours. en-US writes
     "Sep 23", en-GB "23 Sept", and a range has to collapse on the side that
     does not carry the month: "Sep 23–25" against "23–25 Sept". Reading the
     order out of formatToParts keeps that decision with Intl while still
     letting us drop the repeat, which Intl's own formatRange spaces as
     "Sep 23 – 25" -- a step wider than the line above it.

     Across a month boundary both dates carry their month, because "Sep 30–2"
     reads as a typo. */
  function monthLeadsDay() {
    var parts = new Intl.DateTimeFormat(undefined, {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'short'
    }).formatToParts(new Date(Date.UTC(2020, 0, 2)));

    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type === 'month') return true;
      if (parts[i].type === 'day') return false;
    }
    return false;
  }

  function rangeLine(low, high) {
    var sameMonth = low.getUTCFullYear() === high.getUTCFullYear() &&
      low.getUTCMonth() === high.getUTCMonth();
    var full = { day: 'numeric', month: 'short' };

    if (!sameMonth) {
      return 'Estimated delivery ' + format(low, full) + '–' + format(high, full);
    }

    return monthLeadsDay()
      ? 'Estimated delivery ' + format(low, full) + '–' + format(high, { day: 'numeric' })
      : 'Estimated delivery ' + format(low, { day: 'numeric' }) + '–' + format(high, full);
  }

  function render(el) {
    var transit = parseTransit(el.dataset.transit);
    if (!transit) return;

    var holidays = parseHolidays(el.dataset.holidays);

    var now = nowInSingapore();
    if (!now) return;

    /* An unreadable cutoff falls back to -1, which no hour is below, so the
       parcel is dated to the next working day. That is the later of the two
       answers: a missing setting must not promise a dispatch that has already
       closed. */
    var cutoff = parseInt(el.dataset.cutoff, 10);
    if (isNaN(cutoff)) cutoff = -1;

    var beforeCutoff = now.hour < cutoff && isWorkingDay(now.date, holidays);
    var dispatch = beforeCutoff ? now.date : nextWorkingDay(now.date, holidays);
    if (!dispatch) return;

    var low = addWorkingDays(dispatch, transit[0], holidays);
    var high = transit[1] === transit[0] ? low : addWorkingDays(dispatch, transit[1], holidays);
    if (!low || !high) return;

    el.textContent = transit[0] === transit[1] ? singleLine(low) : rangeLine(low, high);
    el.hidden = false;
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-pdp-delivery]');
    for (var i = 0; i < nodes.length; i++) {
      try {
        render(nodes[i]);
      } catch (error) {
        /* Leave it hidden. A thrown formatter is not worth a wrong date. */
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
